<script setup lang="ts">
export type Tournament = {
  custom_data: {};
  description: string;
  duration_ms: number;
  end_time: number;
  hide_leaderboard_min_scores: boolean;
  image1: string;
  image2: string;
  image2_mobile: string;
  instance_id: number;
  is_active: boolean;
  is_can_register: boolean;
  is_cancelled: boolean;
  is_featured: boolean;
  is_finished: boolean;
  is_in_progress: boolean;
  is_upcoming: boolean;
  is_user_registered: boolean;
  min_scores_win: number;
  name: string;
  players_max_count: number;
  players_min_count: number;
  priority: string;
  prize_pool_short: string;
  registration_count: number;
  registration_status: string;
  registration_type: string;
  ribbon: string;
  start_time: number;
  tournament_id: number;
};

export type TournamentInfo = {
  custom_data: {};
  description: string;
  duration_ms: number;
  end_time: number;
  hide_leaderboard_min_scores: boolean;
  image1: string;
  image2: string;
  image2_mobile: string;
  instance_id: number;
  is_active: boolean;
  is_can_register: boolean;
  is_cancelled: boolean;
  is_featured: boolean;
  is_finished: boolean;
  is_in_progress: boolean;
  is_upcoming: boolean;
  is_user_registered: boolean;
  min_scores_win: number;
  name: string;
  players: Array<{
    avatar_url: string;
    is_me: boolean;
    position: number;
    public_username: string;
    scores: number;
  }>;
  players_max_count: number;
  players_min_count: number;
  priority: string;
  prize_pool_short: string;
  prizes: Array<{
    id: number;
    name: string;
    place_from: number;
    place_to: number;
    type: string;
  }>;
  registration_count: number;
  registration_status: string;
  registration_type: string;
  related_games: Array<any>;
  ribbon: string;
  start_time: number;
  tournament_id: number;
};

// State
const tournaments = ref<Tournament[]>([]);
const infoByTournamentId = ref<Record<number, TournamentInfo | null>>({});
const remainingTimes = ref<Record<number, string>>({});
const isLoading = ref(true);
const hasError = ref(false);
const carouselRef = ref<any | null>(null);

const { isMobile } = useLayout();

// Configuration
// Display configuration for LazyWidgetsCarousel
const carouselDisplayConfig = {
  itemsPerRow: {
    320: 2,
    768: 4,
    desktop: 6,
  },
  spaceBetweenItems: {
    320: 4,
    768: 12,
    desktop: 18,
  },
};

const isNavigationLeftButtonDisabled = computed<boolean>(() => {
  return !!carouselRef.value?.isNavigationLeftButtonDisabled;
});

const isNavigationRightButtonDisabled = computed<boolean>(() => {
  return !!carouselRef.value?.isNavigationRightButtonDisabled;
});

// Methods
async function fetchTournaments(): Promise<Tournament[]> {
  if (typeof window._smartico === 'undefined' || !window._smartico.vapi) {
    console.error('_smartico não está disponível');
    hasError.value = true;

    return [];
  }

  try {
    const { isAuthenticated } = useAuthStore();

    if (isAuthenticated) {
      return await window._smartico.api.getTournamentsList();
    } else {
      return await window._smartico.vapi('EN').getTournamentsList();
    }
  } catch (error) {
    console.error('Erro ao buscar torneios:', error);
    hasError.value = true;

    return [];
  }
}

async function loadTournaments() {
  const allTournaments = await fetchTournaments();

  tournaments.value = allTournaments.filter(
    (tournament) => !tournament.is_finished,
  );
}

async function fetchTournamentInfo(
  tournamentInstanceId: number,
): Promise<TournamentInfo | null> {
  if (typeof window._smartico === 'undefined' || !window._smartico.vapi) {
    console.error('_smartico não está disponível');
    hasError.value = true;

    return null;
  }

  try {
    return await window._smartico
      .vapi('EN')
      .getTournamentInstanceInfo(tournamentInstanceId);
  } catch (error) {
    console.error(
      `Erro ao buscar informações do torneio ${tournamentInstanceId}:`,
      error,
    );
    hasError.value = true;

    return null;
  }
}

async function loadTournamentInfo(tournamentInstanceId: number) {
  infoByTournamentId.value[tournamentInstanceId] =
    await fetchTournamentInfo(tournamentInstanceId);
}

function formatPlayerName(username: string): string {
  if (username.length <= 4) return username;

  return username.substring(0, 4) + '***';
}

function formatPrize(player: any, tournament: Tournament): string {
  const info = infoByTournamentId.value[tournament.instance_id];

  if (!info || !info.prizes) return 'R$0,00';

  const prize = info.prizes.find(
    (p) => player.position >= p.place_from && player.position <= p.place_to,
  );

  return prize ? prize.name : 'R$0,00';
}

function getTimeRemainingText(tournament: Tournament): string {
  const now = Date.now();
  const endTime = tournament.end_time;
  const remaining = endTime - now;

  if (tournament.start_time > now) {
    return 'INICIA EM';
  }

  return remaining <= 0 ? '' : 'FALTAM';
}

function getTournamentStatus(tournament: Tournament): string {
  const statuses = [
    { condition: tournament.is_user_registered, label: 'VER RANKING' },
    { condition: tournament.is_finished, label: 'FINALIZADO' },
    { condition: !tournament.is_in_progress, label: 'EM BREVE' },
    { condition: true, label: 'PARTICIPAR' },
  ];

  return statuses.find((status) => status.condition)?.label || 'PARTICIPAR';
}

function formatTimeRemaining(end_time: number): string {
  const now = Date.now();
  let diff = end_time - now;

  if (diff <= 0) return '00:00:00:00';

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  diff -= days * 1000 * 60 * 60 * 24;

  const hours = Math.floor(diff / (1000 * 60 * 60));

  diff -= hours * 1000 * 60 * 60;

  const minutes = Math.floor(diff / (1000 * 60));

  diff -= minutes * 1000 * 60;

  const seconds = Math.floor(diff / 1000);

  const pad = (n: number) => n.toString().padStart(2, '0');

  return `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function handleTournamentClick(tournamentInstanceId: number) {
  const { isAuthenticated } = useAuthStore();
  const { openLogin } = useAuth();

  if (!isAuthenticated) {
    openLogin();

    return;
  }

  navigateTo(localeLink(`/vip/tournaments/${tournamentInstanceId}`));
}

// Lifecycle
onMounted(() => {
  const interval = setInterval(async () => {
    if (window._smartico?.checkInit()) {
      clearInterval(interval);
      isLoading.value = true;
      hasError.value = false; // Reset error state on new attempt

      await loadTournaments();

      if (hasError.value) {
        isLoading.value = false;

        return;
      }

      const now = Date.now();

      tournaments.value.sort((a, b) => {
        // Ordenação por status
        const getStatusWeight = (t: any) => {
          if (t.start_time <= now && t.is_active) return 0; // ativo
          if (t.start_time > now) return 1; // ainda vai começar

          return 2; // já começou, mas inativo
        };

        const statusDiff = getStatusWeight(a) - getStatusWeight(b);

        if (statusDiff !== 0) return statusDiff;

        // Ordenação por prioridade
        const priorityDiff = Number(a.priority) - Number(b.priority);

        if (priorityDiff !== 0) return priorityDiff;

        // Desempate por nome alfabético
        return a.name.localeCompare(b.name);
      });

      await Promise.all(
        tournaments.value.map((tournament) =>
          loadTournamentInfo(tournament.instance_id),
        ),
      );

      isLoading.value = false;
    }
  }, 100);

  setInterval(() => {
    tournaments.value.forEach((tournament) => {
      if (tournament.end_time > Date.now()) {
        remainingTimes.value[tournament.instance_id] = formatTimeRemaining(
          tournament.end_time,
        );
      }
    });
  }, 1000);
});
</script>

<template>
  <div
    v-if="!hasError"
    class="home-tournaments-container"
  >
    <div
      class="flex justify-between items-center [background:linear-gradient(270deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.00)_100%)] rounded-md mb-2.5 lg:mb-4"
    >
      <div
        class="flex items-center h-[31px] lg:h-[45px] justify-center gap-2.5 md:gap-2"
      >
        <div
          class="w-[36px] h-[36px] lg:w-[45px] lg:h-[45px] flex-shrink-0 flex items-center justify-center"
        >
          <NuxtIcon
            class="text-[36px] lg:text-[45px] w-full h-full"
            :filled="true"
            name="home/tournament"
          />
        </div>
        <p :class="$style.titleText">Torneios</p>
      </div>

      <NuxtLink
        class="flex items-center justify-center ml-auto gap-1 px-2.5"
        no-prefetch
        to="/vip/tournaments"
      >
        <span class="text-[11px] md:px-2.5 text-white font-bold leading-4"
          >VER MAIS</span
        >
        <NuxtIcon
          v-if="isMobile"
          class="text-[15px] text-white"
          name="perfil-oficial/next"
        />
      </NuxtLink>

      <div :class="$style.actions">
        <div class="flex items-center justify-center">
          <button
            v-if="!isMobile"
            class="flex items-center justify-center rounded-full text-white disabled:opacity-50 px-2.5"
            :disabled="isNavigationLeftButtonDisabled"
            @click="carouselRef?.scrollToLeft(427)"
          >
            <NuxtIcon
              class="text-[15px]"
              name="perfil-oficial/back"
            />
          </button>
          <button
            v-if="!isMobile"
            class="flex items-center justify-center rounded-full bg-gray-700 text-white disabled:opacity-50 px-2.5"
            :disabled="isNavigationRightButtonDisabled"
            @click="carouselRef?.scrollToRight(427)"
          >
            <NuxtIcon
              class="text-[15px]"
              name="perfil-oficial/next"
            />
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="!hasError"
      class="h-[140px] md:h-[210px] mb-4"
      style="min-height: 140px"
    >
      <LazyWidgetsCarousel
        v-if="!isLoading"
        ref="carouselRef"
        :display="carouselDisplayConfig"
        :items="tournaments || []"
        slider-fade="both"
      >
        <template #item="{ item: tournament }">
          <div
            v-if="tournament"
            class="flex w-[333px] h-[140px] ml-0.5 md:w-[425px] md:h-[210px]"
            :class="{
              grayscale: !tournament.is_active,
            }"
          >
            <div
              class="relative w-[140px] h-[140px] md:w-[210px] md:h-[210px]"
              style="aspect-ratio: 1/1"
            >
              <!-- Fundo com gradiente centralizado -->
              <div
                class="rounded-md absolute w-[140px] h-[140px] md:w-[210px] md:h-[210px]"
                style="
                  background: linear-gradient(
                    90deg,
                    #ff3400 0%,
                    rgba(255, 52, 0, 0) 100%
                  );
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  z-index: 1;
                "
              ></div>
              <!-- Imagem Torneio -->
              <img
                :alt="`Tournament-${tournament.tournament_id}`"
                class="rounded-md object-cover absolute w-[137px] h-[137px] md:w-[207px] md:h-[207px]"
                :class="{
                  grayscale: !tournament.is_in_progress,
                }"
                draggable="false"
                height="207"
                loading="lazy"
                :src="tournament.image2_mobile"
                style="
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  display: block;
                  z-index: 2;
                  aspect-ratio: 1/1;
                "
                width="207"
              />

              <!-- Sombra linear só embaixo -->
              <div
                class="absolute w-[137px] h-[137px] md:w-[207px] md:h-[207px]"
                style="
                  top: 50%;
                  left: 50%;
                  transform: translate(-50%, -50%);
                  pointer-events: none;
                  border-radius: 0.375rem;
                  overflow: hidden;
                  z-index: 3;
                  aspect-ratio: 1/1;
                "
              >
                <div
                  style="
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    width: 100%;
                    height: 60%;
                    pointer-events: none;
                    background: linear-gradient(
                      to top,
                      rgba(0, 0, 0, 1) 0%,
                      rgba(0, 0, 0, 0.6) 75%,
                      rgba(0, 0, 0, 0) 100%
                    );
                    border-radius: 0 0 0.375rem 0.375rem;
                  "
                ></div>
              </div>
              <!-- Textos dentro da imagem -->
              <!-- Nome operadora -->
              <div
                class="text-[10px] leading-[12px] w-full text-white absolute top-[1px] z-10 text-center"
              >
                <span
                  class="font-bold px-[5px] py-[3px] md:px-[10px] md:py-[6px] justify-center top-0 z-10 text-[8px] md:text-[9px] leading-[10px] rounded-b-lg uppercase"
                  style="
                    background: radial-gradient(
                      136.67% 136.67% at 36.76% -16.67%,
                      #ff704a 0%,
                      #ff3400 100%
                    );
                  "
                  >{{ tournament.ribbon }}</span
                >
              </div>
              <!-- Nome do Torneio e Prêmio -->
              <div
                class="text-[10px] md:text-[12px] leading-[12px] md:leading-[15px] w-full text-white absolute bottom-[12px] md:bottom-[15px] z-10 px-[8px] md:px-[15px] font-semibold"
                style="white-space: normal; overflow-wrap: break-word"
              >
                <span
                  v-if="tournament.registration_count"
                  class="flex items-center mb-0.5"
                >
                  <NuxtIcon
                    class="text-[#00ff00] -mt-0.5 mr-1 text-[12px]"
                    name="users"
                  />
                  {{ tournament.registration_count ?? '---' }}</span
                >
                <span class="font-extrabold text-[11px] md:text-[13px]">{{
                  tournament.name
                }}</span
                ><br />
                <span>{{ 'Prêmio: ' + tournament.prize_pool_short }}</span>
              </div>
            </div>

            <div
              class="flex-1 rounded-r-md bg-[linear-gradient(270deg,_rgba(255,255,255,0.05)_0%,_rgba(255,255,255,0)_100%)] flex flex-col p-[5px] pt-0 md:pt-1 h-[140px] md:h-[210px] overflow-hidden"
            >
              <table class="text-white text-xs md:text-lg w-full">
                <thead
                  :class="{
                    hidden: !tournament.is_in_progress && tournament.is_active,
                  }"
                >
                  <tr
                    class="text-left text-[9px] md:text-[10px] leading-[11px] md:leading-[10px]"
                  >
                    <th
                      class="w-[28px] md:w-[45px] text-center p-[5px] pl-[12px] md:pl-[10px]"
                      >#</th
                    >
                    <th class="px-[5px] w-[87px] md:w-[75px]">Jogador</th>
                    <th class="w-[140px] md:w-[120px]">Prêmio</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(player, playerIndex) in infoByTournamentId[
                      tournament.instance_id
                    ]?.players?.slice(0, 3)"
                    :key="playerIndex"
                    class="text-[9px] md:text-[12px] leading-[11px] md:leading-[10px] md:font-semibold"
                    :class="{
                      'bg-[#D6ED16] text-black': player.is_me,
                      'bg-[#FFFFFF1A]': playerIndex % 2 === 0 && !player.is_me,
                    }"
                  >
                    <td
                      class="text-center rounded-l-md h-[26px] md:h-[32px] pl-[5px] w-[28px] md:w-[45px]"
                    >
                      <div class="flex items-center justify-center h-full">
                        <img
                          v-if="player.position"
                          :alt="`${player.position}º`"
                          class="w-[28px] md:w-[26px]"
                          draggable="false"
                          loading="lazy"
                          :src="
                            getAssetsUrlForCDN(
                              `/assets/gamification/t-place-${player.position}.png`,
                            )
                          "
                        />
                        <span
                          v-else
                          class="text-center text-[9px] md:text-[12px]"
                          >{{ player.position }}</span
                        >
                      </div>
                    </td>
                    <td
                      class="text-[9px] md:text-[12px] leading-[11px] md:leading-[10px] py-[5px] px-[5px] h-[26px] md:h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis max-w-[58px] md:max-w-[50px]"
                      :title="formatPlayerName(player.public_username)"
                      >{{ formatPlayerName(player.public_username) }}</td
                    >
                    <td
                      class="rounded-r-md text-[9px] md:text-[12px] leading-[11px] md:leading-[10px] h-[26px] md:h-[22px] align-middle whitespace-nowrap overflow-hidden text-ellipsis max-w-0"
                      >{{ formatPrize(player, tournament) }}</td
                    >
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colspan="3">
                      <div
                        class="flex md:flex-wrap items-center pt-[5px] md:pt-[10px] rounded-md gap-2 md:gap-3"
                      >
                        <div
                          class="w-5 h-5 p-[4px] md:w-8 md:h-8 md:p-[7px] self-center rounded-md bg-[#FFFFFF1A]"
                        >
                          <NuxtIcon name="timer" />
                        </div>

                        <div
                          class="max-w-[58px] md:max-w-[50px] flex-1 md:flex md:gap-2"
                        >
                          <div
                            class="-ml-1 leading-[11px] text-[9px] md:text-[12px] md:opacity-70 md:font-bold"
                            :class="{
                              'md:ml-[30px]': tournament.is_in_progress,
                              'md:ml-0 whitespace-nowrap':
                                !tournament.is_in_progress,
                            }"
                            >{{ getTimeRemainingText(tournament) }}</div
                          >
                          <div
                            class="text-[9px] md:text-[12px] leading-[11px] md:leading-[10px] font-bold -ml-1 tracking-wide w-[70px] md:w-[60px]"
                            :class="{
                              'text-[#D6ED16]': !tournament.is_in_progress,
                            }"
                            >{{
                              remainingTimes[tournament.instance_id] || '...'
                            }}</div
                          >
                        </div>

                        <button
                          class="p-[5px] md:p-[9px] rounded-md leading-[14px] md:leading-[12px] text-[12px] md:text-[13px] w-full transition-transform duration-300 hover:scale-[1.01] font-extrabold"
                          :class="{
                            'bg-[#0808B3] text-white text-[8px] md:text-[7px] scale-1 px-0':
                              tournament.is_user_registered,
                            'bg-white text-[#0808B3]':
                              !tournament.is_user_registered,
                          }"
                          @click="handleTournamentClick(tournament.instance_id)"
                        >
                          {{ getTournamentStatus(tournament) }}
                        </button>
                      </div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </template>
      </LazyWidgetsCarousel>
      <!-- Skeleton enquanto carrega -->
      <LazyWidgetsCarousel
        v-else-if="isLoading && !hasError"
        :display="carouselDisplayConfig"
        :enable-navigation-buttons="true"
        :items="Array(6)"
        slider-fade="both"
      >
        <template #item>
          <div
            class="flex w-[333px] h-[140px] md:w-[425px] md:h-[210px] ml-0.5 animate-pulse"
          >
            <!-- Bloco Esquerdo (Simula a área da imagem) -->
            <div
              class="w-[140px] h-[140px] md:w-[210px] md:h-[210px] rounded-md overflow-hidden"
              style="background-color: #4a5568; aspect-ratio: 1/1"
            >
              <!-- Placeholder para o texto superior (ribbon) -->
              <div
                class="w-3/4 h-[15px] rounded-b-lg mx-auto mt-0.5"
                style="background-color: #6b7280"
              ></div>
            </div>

            <!-- Bloco Direito (Simula a área da tabela) -->
            <div
              class="flex-1 rounded-r-md flex flex-col p-[5px] pt-0 h-[140px] md:h-[210px] overflow-hidden"
              style="background-color: #2d3748"
            >
              <!-- Placeholder para as linhas da tabela (genérico) -->
              <div
                v-for="j in 3"
                :key="j"
                class="h-[26px] md:h-[32px] rounded mb-1"
                :style="{
                  'background-color': j % 2 === 0 ? '#4A5568' : '#6B7280',
                }"
              ></div>
              <!-- Placeholder para o botão do rodapé -->
              <div class="flex justify-end mt-auto pt-[5px]">
                <div
                  class="w-[93px] md:w-[80px] h-[29px] md:h-[25px] rounded-md"
                  style="background-color: #6b7280"
                ></div>
              </div>
            </div>
          </div>
        </template>
      </LazyWidgetsCarousel>
    </div>
  </div>
</template>

<style module lang="postcss">
.navigationButton {
  @apply hover:text-primary cursor-pointer text-texts/60 p-[5px] rounded-md bg-[#09173c];
  user-select: none;

  & + .navigationButton {
    @apply ml-3;
  }

  &.isNavigationButtonDisabled {
    @apply text-texts/20 cursor-not-allowed;
  }
}

.titleText {
  @apply text-white text-[16px] lg:text-[20px] font-bold leading-4;
  user-select: none;
}

.actions {
  @apply flex items-center gap-3;
}

.title-torneios {
  user-select: none;
}

/* Apply to the entire header container to prevent selection on all child elements */
.title-torneios > * {
  user-select: none;
}

/* Margin já aplicado no Carousel.vue */
</style>
